import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, updateDoc, doc, increment, setDoc } from 'firebase/firestore';

const GamificationContext = createContext();

export function useGamification() {
  return useContext(GamificationContext);
}

export function GamificationProvider({ children }) {
  const [achievements, setAchievements] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Point values for different actions
  const POINT_VALUES = {
    ASK_QUESTION: 10,
    ANSWER_QUESTION: 20,
    UPLOAD_MATERIAL: 50,
    DOWNLOAD_MATERIAL: 5,
    DAILY_LOGIN: 5,
    PROFILE_COMPLETE: 25,
    ACHIEVEMENT_UNLOCK: 100
  };

  // Achievement thresholds
  const ACHIEVEMENT_THRESHOLDS = {
    ask_10: { type: 'questions_asked', threshold: 10 },
    ask_50: { type: 'questions_asked', threshold: 50 },
    answer_10: { type: 'questions_answered', threshold: 10 },
    answer_50: { type: 'questions_answered', threshold: 50 },
    upload_5: { type: 'materials_uploaded', threshold: 5 },
    upload_20: { type: 'materials_uploaded', threshold: 20 },
    download_10: { type: 'materials_downloaded', threshold: 10 },
    login_7: { type: 'consecutive_logins', threshold: 7 },
    login_30: { type: 'consecutive_logins', threshold: 30 }
  };

  // Initialize Firestore collections
  const initializeCollections = async () => {
    try {
      // Initialize achievements collection
      const achievementsRef = collection(db, 'achievements');
      const achievementsSnapshot = await getDocs(achievementsRef);
      
      if (achievementsSnapshot.empty) {
        const defaultAchievements = [
          { id: 'ask_10', title: 'Curious Mind', description: 'Ask 10 questions', points: 100 },
          { id: 'ask_50', title: 'Knowledge Seeker', description: 'Ask 50 questions', points: 500 },
          { id: 'answer_10', title: 'Helpful Soul', description: 'Answer 10 questions', points: 200 },
          { id: 'answer_50', title: 'Community Expert', description: 'Answer 50 questions', points: 1000 },
          { id: 'upload_5', title: 'Resource Provider', description: 'Upload 5 study materials', points: 250 },
          { id: 'upload_20', title: 'Content Creator', description: 'Upload 20 study materials', points: 1000 },
          { id: 'download_10', title: 'Active Learner', description: 'Download 10 materials', points: 100 },
          { id: 'login_7', title: 'Regular User', description: 'Login for 7 consecutive days', points: 100 },
          { id: 'login_30', title: 'Dedicated Member', description: 'Login for 30 consecutive days', points: 500 },
          { id: 'profile_complete', title: 'Profile Perfectionist', description: 'Complete your profile', points: 50 }
        ];

        for (const achievement of defaultAchievements) {
          await setDoc(doc(achievementsRef, achievement.id), achievement);
        }
      }

      // Initialize leaderboard collection
      const leaderboardRef = collection(db, 'leaderboard');
      const leaderboardSnapshot = await getDocs(leaderboardRef);
      
      if (leaderboardSnapshot.empty) {
        await setDoc(doc(leaderboardRef, 'default'), {
          score: 0,
          updatedAt: new Date()
        });
      }

      // Initialize userProgress collection
      const userProgressRef = collection(db, 'userProgress');
      const userProgressSnapshot = await getDocs(userProgressRef);
      
      if (userProgressSnapshot.empty) {
        await setDoc(doc(userProgressRef, 'default'), {
          points: 0,
          achievements: [],
          questions_asked: 0,
          questions_answered: 0,
          materials_uploaded: 0,
          materials_downloaded: 0,
          consecutive_logins: 0,
          last_login: null,
          updatedAt: new Date()
        });
      }
    } catch (err) {
      console.error('Error initializing collections:', err);
      setError(err.message);
    }
  };

  // Fetch gamification data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await initializeCollections();
        
        const [achievementsSnapshot, leaderboardSnapshot, userProgressSnapshot] = await Promise.all([
          getDocs(collection(db, 'achievements')),
          getDocs(query(collection(db, 'leaderboard'), orderBy('score', 'desc'))),
          getDocs(collection(db, 'userProgress'))
        ]);

        setAchievements(achievementsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLeaderboard(leaderboardSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const progressData = {};
        userProgressSnapshot.docs.forEach(doc => {
          progressData[doc.id] = doc.data();
        });
        setUserProgress(progressData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update user progress
  const updateUserProgress = async (userId, progressData) => {
    try {
      setLoading(true);
      const userRef = doc(db, 'userProgress', userId);
      const currentProgress = userProgress[userId] || {
        points: 0,
        achievements: [],
        questions_asked: 0,
        questions_answered: 0,
        materials_uploaded: 0,
        materials_downloaded: 0,
        consecutive_logins: 0,
        last_login: null
      };

      const updatedProgress = {
        ...currentProgress,
        ...progressData,
        updatedAt: new Date()
      };

      await updateDoc(userRef, updatedProgress);
      setUserProgress(prev => ({
        ...prev,
        [userId]: updatedProgress
      }));

      // Check for new achievements
      await checkAchievements(userId, updatedProgress);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Check and award achievements
  const checkAchievements = async (userId, progress) => {
    const newAchievements = [];
    
    Object.entries(ACHIEVEMENT_THRESHOLDS).forEach(([achievementId, { type, threshold }]) => {
      if (progress[type] >= threshold && !progress.achievements.includes(achievementId)) {
        newAchievements.push(achievementId);
      }
    });

    if (newAchievements.length > 0) {
      await awardAchievement(userId, newAchievements);
    }
  };

  // Award achievement
  const awardAchievement = async (userId, achievementIds) => {
    try {
      setLoading(true);
      const userRef = doc(db, 'userProgress', userId);
      const currentProgress = userProgress[userId] || { achievements: [] };

      const newAchievements = [...currentProgress.achievements, ...achievementIds];
      const pointsEarned = achievementIds.length * POINT_VALUES.ACHIEVEMENT_UNLOCK;

      await updateDoc(userRef, {
        achievements: newAchievements,
        points: increment(pointsEarned),
        pointHistory: [...(currentProgress.pointHistory || []), {
          points: pointsEarned,
          reason: `Achievement${achievementIds.length > 1 ? 's' : ''} unlocked`,
          timestamp: new Date()
        }],
        updatedAt: new Date()
      });

      // Update leaderboard
      await updateLeaderboard(userId, currentProgress.points + pointsEarned);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update points for specific actions
  const updatePoints = async (userId, action, reason) => {
    try {
      setLoading(true);
      const points = POINT_VALUES[action];
      if (!points) return;

      const userRef = doc(db, 'userProgress', userId);
      const currentProgress = userProgress[userId] || { points: 0 };

      await updateDoc(userRef, {
        points: increment(points),
        pointHistory: [...(currentProgress.pointHistory || []), {
          points,
          reason,
          timestamp: new Date()
        }],
        updatedAt: new Date()
      });

      // Update leaderboard
      await updateLeaderboard(userId, currentProgress.points + points);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update leaderboard
  const updateLeaderboard = async (userId, score) => {
    try {
      setLoading(true);
      const leaderboardRef = doc(db, 'leaderboard', userId);
      await updateDoc(leaderboardRef, {
        score,
        updatedAt: new Date()
      });

      // Refresh leaderboard data
      const leaderboardSnapshot = await getDocs(query(collection(db, 'leaderboard'), orderBy('score', 'desc')));
      setLeaderboard(leaderboardSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    achievements,
    leaderboard,
    userProgress,
    loading,
    error,
    updateUserProgress,
    awardAchievement,
    updatePoints,
    updateLeaderboard,
    POINT_VALUES
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
} 